import React, { useCallback, useEffect, useMemo, useRef } from 'react'

import cx from 'classnames'
import KSC from 'conversations/components/KeyboardShortcutsSidebar/keyboardShortcutsConfig'
import ConversationListItem from 'conversations/components/NewChatSidebar/ConversationListItem'
import { useConversationId } from 'conversations/hooks/useConversationId'
import useConversationIdsBySection from 'conversations/hooks/useConversationIdsBySection'
import { useSectionConfigProperty } from 'conversations/hooks/useSections'
import useSocketReconnect from 'conversations/hooks/useSocketReconnect'
import {
  fetchConversationListLoading,
  getHasMoreSidebarConversations,
  getNextSidebarCursor,
} from 'conversations/modules/conversation/selectors'
import { getResponseTimerDuration } from 'conversations/modules/message/selectors'
import { Pill } from 'conversations/utils/pillUtil'
import Hotkeys from 'react-hot-keys'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import { CSSTransition, TransitionGroup } from 'react-transition-group'
import { Dispatch } from 'redux'
import { fetchAllConversationsAsync } from 'shared/store/actions/conversation'
import { track } from 'shared/store/actions/tracking'
import { QueryWithoutFilterGroups } from 'shared/typings/conversations/query'

import { RootAction, RootState } from 'typesafe-actions'

import styles from './styles.module.css'
import conversationListItemAnimationStyles from 'conversations/css/animationStyles/conversationListItemAnimation.module.css'

type Direction = 'previous' | 'next'

const LOAD_MORE_LENGTH = 5

const LoadingState: React.FC<{ num: number }> = ({ num }) => (
  <div className={styles.loadingState}>
    <SkeletonTheme color="var(--color-p1-5)" highlightColor="var(--color-p1-3)">
      {[...new Array(num)].map((_, idx) => (
        <div key={idx} className={styles.skeletonContainer}>
          <div>
            <Skeleton circle={true} height={32} width={32} />
          </div>
          <div className={styles.skeletonItem}>
            <div className={styles.topLine}>
              <Skeleton height="10px" width="150px" />
              <Skeleton height="10px" width="20px" />
            </div>
            <Skeleton height="10px" />
            <Skeleton height="10px" width="50px" />
          </div>
        </div>
      ))}
    </SkeletonTheme>
  </div>
)

interface Props {
  query: QueryWithoutFilterGroups
  selectedStatus: Pill
  viewType: string
  emptyText?: string
  sectionID?: string
  inboxId?: number
  sectionLength: number
  onSectionLengthChange: (length: number) => void
}

const FilteredConversationList: React.FC<Props> = ({
  query,
  viewType,
  emptyText = 'No conversations to show',
  sectionID,
  selectedStatus,
  inboxId,
  sectionLength,
  onSectionLengthChange,
}): JSX.Element => {
  useSocketReconnect(() => {
    dispatch(track({ name: 'SocketReconnect' }))
  })
  const sectionType = useSectionConfigProperty(sectionID, 'type')
  const subscriptionId = useSectionConfigProperty(sectionID, 'subscriptionId')
  const dispatch = useDispatch<Dispatch<RootAction>>()
  const history = useHistory()
  const selectedConversationId = useConversationId() || 0

  const conversationIds = useConversationIdsBySection(sectionID ?? '', selectedStatus, inboxId)
  const hasMoreConversations = useSelector<RootState, boolean | undefined>(
    getHasMoreSidebarConversations(viewType)
  )
  const nextSidebarCursor = useSelector<RootState, string | undefined>((state) =>
    getNextSidebarCursor(state, viewType)
  )

  const isInitialMount = useRef(true)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
    }
    dispatch(
      fetchAllConversationsAsync.request({
        query,
        cursor: undefined,
        viewType,
        subscriptionId,
        refreshState: isInitialMount.current,
      })
    )
  }, [dispatch, query, viewType, subscriptionId])

  const fetchNextConversationList = useCallback(() => {
    dispatch(
      fetchAllConversationsAsync.request({
        query,
        cursor: nextSidebarCursor,
        viewType,
        subscriptionId,
        refreshState: false,
      })
    )
    onSectionLengthChange(sectionLength + LOAD_MORE_LENGTH)
  }, [
    dispatch,
    query,
    nextSidebarCursor,
    viewType,
    subscriptionId,
    onSectionLengthChange,
    sectionLength,
  ])

  const allowKeyboardEventsFromAllFields = useCallback(() => true, [])

  const hasActiveConversations = conversationIds.length > 0 || hasMoreConversations

  const getNextConversationId = useCallback(
    (direction: Direction) => {
      const lastIndex = conversationIds.length - 1
      if (lastIndex === 0) return conversationIds[0]
      if (selectedConversationId === 0) {
        return direction === 'next' ? conversationIds[0] : conversationIds[lastIndex]
      }
      const selectedIndex = conversationIds.indexOf(selectedConversationId)
      return direction === 'next'
        ? conversationIds[selectedIndex + 1] ?? conversationIds[0]
        : conversationIds[selectedIndex - 1] ?? conversationIds[lastIndex]
    },
    [conversationIds, selectedConversationId]
  )

  const prevKeyboardConfig = KSC.navigation.previous_conversation
  const nextKeyboardConfig = KSC.navigation.next_conversation

  const onKeyDown = useCallback(
    (keyName: string) => {
      if (hasActiveConversations) {
        if (keyName === prevKeyboardConfig.shortcut) {
          prevKeyboardConfig.track()
          history.push(`/conversations/${getNextConversationId('previous')}`)
        } else {
          nextKeyboardConfig.track()
          history.push(`/conversations/${getNextConversationId('next')}`)
        }
      }
    },
    [hasActiveConversations, getNextConversationId, history, prevKeyboardConfig, nextKeyboardConfig]
  )

  const slicedConversationIds = useMemo(
    () => conversationIds.slice(0, sectionLength),
    [sectionLength, conversationIds]
  )
  const hasMoreSlicedConversations = conversationIds.length > slicedConversationIds.length

  const isLoading = useSelector(fetchConversationListLoading(viewType))

  const TransitionConversationComponent: React.FC<{ convoId: number }> = ({
    convoId,
    ...rest
  }): JSX.Element => {
    const responseTimerDuration = useSelector(getResponseTimerDuration(convoId))
    const showTimer =
      selectedStatus === 'open' && sectionType === 'my-convos' && !!responseTimerDuration
    const timerClasses = {
      enter: conversationListItemAnimationStyles.enterTimer,
      enterActive: conversationListItemAnimationStyles.enterActiveTimer,
      enterDone: conversationListItemAnimationStyles.enterDoneTimer,
      exit: conversationListItemAnimationStyles.exitTimer,
    }
    const animationClasses = {
      ...conversationListItemAnimationStyles,
      ...(showTimer ? timerClasses : {}),
    }
    return (
      <CSSTransition timeout={300} classNames={animationClasses} {...rest}>
        {(transitionState) => (
          <div className={cx(styles.convoItemWrapper, 'fade', `fade-${transitionState}`)}>
            <ConversationListItem
              responseTimerDuration={responseTimerDuration}
              selectedStatus={selectedStatus}
              selected={convoId === selectedConversationId}
              conversationId={convoId}
              sectionID={sectionID ?? ''}
            />
          </div>
        )}
      </CSSTransition>
    )
  }

  return (
    <>
      {isLoading ? (
        <LoadingState num={5} />
      ) : !hasActiveConversations ? (
        <div data-ocweb-id="NoConversationsToShow" className={styles.noConversationsToShow}>
          {emptyText}
        </div>
      ) : (
        <>
          <Hotkeys
            keyName={`${prevKeyboardConfig.shortcut}, ${nextKeyboardConfig.shortcut}`}
            onKeyDown={onKeyDown}
            filter={allowKeyboardEventsFromAllFields}>
            <div
              data-ocweb-id={`ConversationList-${sectionID}`}
              className={styles.conversationList}>
              <TransitionGroup
                appear={true}
                enter={true}
                exit={true}
                key={viewType}
                component={null}>
                {slicedConversationIds.map((conversationId) => (
                  <TransitionConversationComponent convoId={conversationId} key={conversationId} />
                ))}
              </TransitionGroup>
            </div>
          </Hotkeys>
          {(hasMoreConversations || hasMoreSlicedConversations) && (
            <div
              className={styles.loadMoreConversationsButton}
              onClick={fetchNextConversationList}
              data-ocweb-id="MoreSidebarConversations">
              Load more conversations
            </div>
          )}
        </>
      )}
    </>
  )
}

export default FilteredConversationList
